import { Service, inject } from "@angular/core";
import {GuruEntity} from "../model/guru-entity";
import {PerformanceDefaults} from "../model/performance-defaults";
import {ModelService} from "./model-service";
import {firstValueFrom, Observable} from "rxjs";
import {SelectionUtils} from "../configurator/selection-utils";
import {StringUtils} from "../utils/string-utils";
import {environment} from "../../environments/environment";
import {HttpClient} from "@angular/common/http";
import {ClmPerformanceInput} from "../model/clm-performance-input";
import {ClmPerformanceResponse} from "../model/clm-performance-response";
import {ClmPerformanceOutput} from "../model/clm-performance-output";
import {ClmElectricalOutput} from "../model/clm-electrical-output";
import {ClmBeltDriveOutput} from "../model/clm-belt-drive-output";

@Service()
export class ClmPerformanceService {
  private readonly modelService = inject(ModelService);
  private readonly http = inject(HttpClient);


  private readonly baseUrl: string = environment.clmPerformanceBaseUrl;

  async initializePerformanceValues(engineKey: string, performanceDefaults: PerformanceDefaults, selections: GuruEntity[], isNew = false): Promise<void> {
    if (isNew) {
      await this.changeValueSelection(engineKey, selections, 'USAltitude', performanceDefaults.altitude.toString());
      await this.changeValueSelection(engineKey, selections, 'USCFluid', performanceDefaults.fluidType);
      await this.changeValueSelection(engineKey, selections, 'USCFluidMix', performanceDefaults.antifreezeConcentration.toString());
      await this.changeValueSelection(engineKey, selections, 'USCEDB', performanceDefaults.coolingDryBulbTemperature.toString());
      await this.changeValueSelection(engineKey, selections, 'USCEWB', performanceDefaults.coolingWetBulbTemperature.toString());
      await this.changeValueSelection(engineKey, selections, 'USCEWT', performanceDefaults.coolingEnteringWaterTemperature.toString());
      await this.changeValueSelection(engineKey, selections, 'USHEDB', performanceDefaults.heatingDryBulbTemperature.toString());
      await this.changeValueSelection(engineKey, selections, 'USHEWT', performanceDefaults.heatingEnteringWaterTemperature.toString());
      await this.changeValueSelection(engineKey, selections, 'USCWFR', performanceDefaults.desiredFluidFlow.toString());
    }
    await this.changeValueSelection(engineKey, selections, 'USDFRTOL', performanceDefaults.desiredFluidFlowTolerance.toString());
    await this.changeValueSelection(engineKey, selections, 'USDFR', performanceDefaults.desiredFluidFlow.toString());
    await this.changeValueSelection(engineKey, selections, 'USMinimumEer', performanceDefaults.minimumEquipmentEer.toString());
    await this.changeValueSelection(engineKey, selections, 'USCWFRTOL', performanceDefaults.desiredFluidFlowTolerance.toString());
    await this.changeValueSelection(engineKey, selections, 'USCFAirflowLT', performanceDefaults.coolingActualAirflowUnderTolerance.toString());
    await this.changeValueSelection(engineKey, selections, 'USCFAirflowUT', performanceDefaults.coolingActualAirflowOverTolerance.toString());
    await this.changeValueSelection(engineKey, selections, 'USCWTotLT', performanceDefaults.coolingTotalCapacityUnderTolerance.toString());
    await this.changeValueSelection(engineKey, selections, 'USCWTotUT', performanceDefaults.coolingTotalCapacityOverTolerance.toString());
    await this.changeValueSelection(engineKey, selections, 'USCWSensLT', performanceDefaults.sensibleCapacityUnderTolerance.toString());
    await this.changeValueSelection(engineKey, selections, 'USCWSensUT', performanceDefaults.sensibleCapacityOverTolerance.toString());
    await this.changeValueSelection(engineKey, selections, 'USCLDBLT', performanceDefaults.coolingLeavingAirDryBulbUnderTolerance.toString());
    await this.changeValueSelection(engineKey, selections, 'USCLDBUT', performanceDefaults.coolingLeavingAirDryBulbOverTolerance.toString());
    await this.changeValueSelection(engineKey, selections, 'USHFAirflowLT', performanceDefaults.heatingActualAirflowUnderTolerance.toString());
    await this.changeValueSelection(engineKey, selections, 'USHFAirflowUT', performanceDefaults.heatingActualAirflowOverTolerance.toString());
    await this.changeValueSelection(engineKey, selections, 'USHWTotLT', performanceDefaults.heatingTotalCapacityUnderTolerance.toString());
    await this.changeValueSelection(engineKey, selections, 'USHWTotUT', performanceDefaults.heatingTotalCapacityOverTolerance.toString());
  }

  private async changeValueSelection(engineKey: string, selections: GuruEntity[], selectionName: string, value: string): Promise<void> {
    const selection: GuruEntity = SelectionUtils.getEntity(selections, selectionName);
    if (StringUtils.isNotBlank(selection?.Name)) {
      selection.Value = value;
      await this.setEngineValue(engineKey, selectionName, value);
    }
  }

  private async setEngineValue(engineKey: string, entityName: string, value: string): Promise<string> {
    return await firstValueFrom<string>(this.modelService.setValue(entityName, engineKey, value));
  }

  async getUnitPerformance(engineKey: string, selections: GuruEntity[]): Promise<ClmPerformanceResponse> {
    const updatedSelections: GuruEntity[] = await this.executeHierarchy(engineKey, 'hierPerformanceInputs');
    for (const selection of updatedSelections) {
      const existingSelection: GuruEntity = SelectionUtils.getEntity(selections, selection.Name);
      if (existingSelection) {
        existingSelection.Value = selection.Value;
      } else {
        selections.push(selection);
      }
    }
    const input: ClmPerformanceInput = await this.buildPerformanceInput(engineKey, selections);
    return await firstValueFrom<ClmPerformanceResponse>(this.getPerformanceResponse(input));
  }

  private async executeHierarchy(engineKey: string, hierarchyName: string): Promise<GuruEntity[]> {
    return await firstValueFrom<GuruEntity[]>(this.modelService.executeHierarchy(engineKey, hierarchyName));
  }

  async setPerformanceResponseValues(engineKey: string, response: ClmPerformanceResponse, userSelections: GuruEntity[]): Promise<void> {
    const calculatedFan: GuruEntity = SelectionUtils.getEntity(userSelections, 'USCalculatedFan');
    if (StringUtils.isNotBlank(calculatedFan?.Name)) {
      const fanSpeedProfile: string = (response?.userSelection?.fanSpeedProfile || 'DEF');
      await this.setEngineValue(engineKey, 'USCalculatedFan', fanSpeedProfile);
      calculatedFan.Value = fanSpeedProfile;
    }
    if (response.coolingData) {
      await this.setCoolingResponseValues(engineKey, response.coolingData);
    }
    if (response.heatingData) {
      await this.setHeatingResponseValues(engineKey, response.heatingData);
    }
    if (response.electricalData) {
      await this.setElectricalResponseValues(engineKey, response.electricalData);
    }
    if (response.beltDriveSelection) {
      await this.setBeltDriveResponseValues(engineKey, response.beltDriveSelection);
    }
  }

  private async setBeltDriveResponseValues(engineKey: string, outputs: ClmBeltDriveOutput): Promise<void> {
    await this.setEngineValue(engineKey, 'RCFSheave', outputs.sheave);
    await this.setEngineValue(engineKey, 'RCFRPM', outputs.revolutionsPerMinute);
    await this.setEngineValue(engineKey, 'RCFPower', outputs.blowerHorsepower);
  }

  private async setElectricalResponseValues(engineKey: string, outputs: ClmElectricalOutput): Promise<void> {
    await this.setEngineValue(engineKey, 'RCFAmp', outputs.fanFullLoadAmps);
    await this.setEngineValue(engineKey, 'RCUMFS', outputs.maxFuseHACR);
    await this.setEngineValue(engineKey, 'RCUMCA', outputs.minimumCircuitAmps);
    await this.setEngineValue(engineKey, 'RCUFLA', outputs.totalFullLoadAmps);
    await this.setEngineValue(engineKey, 'RCRatedVoltage', outputs.ratedVoltage);
    await this.setEngineValue(engineKey, 'RCMinMaxVolt', outputs.minMaxVoltage);
    await this.setEngineValue(engineKey, 'RCCompQty', outputs.compressorQuantity);
    await this.setEngineValue(engineKey, 'RCCompLoadAmp', outputs.compressorRatedLoadAmps);
    await this.setEngineValue(engineKey, 'RCCompLockedRotorAmp', outputs.compressorLockedRotorAmps);
    await this.setEngineValue(engineKey, 'RCFanQty', outputs.fanQuantity);
    await this.setEngineValue(engineKey, 'RCUFanFLA', outputs.totalFullLoadAmpsFan);
    await this.setEngineValue(engineKey, 'RCUFanMCA', outputs.minimumCircuitAmpsFan);
    await this.setEngineValue(engineKey, 'RCUFanMFS', outputs.maxFuseHACRFan);
    await this.setEngineValue(engineKey, 'RCHoseKitDia', outputs.hoseKitSize);
    await this.setEngineValue(engineKey, 'RCHoseKitSizeM', outputs.hoseKitSizeM);
  }

  private async setHeatingResponseValues(engineKey: string, outputs: ClmPerformanceOutput): Promise<void> {
    await this.setEngineValue(engineKey, 'RCHWFR', outputs.fluidFlow);
    await this.setEngineValue(engineKey, 'RCHFAirflow', outputs.airflow);
    await this.setEngineValue(engineKey, 'RCHTot', outputs.totalCapacity);
    await this.setEngineValue(engineKey, 'RCHLDB', outputs.leavingDryBulbTemperature);
    await this.setEngineValue(engineKey, 'RCHWPD', outputs.pressureDrop);
    await this.setEngineValue(engineKey, 'RCHLWT', outputs.leavingWaterTemperature);
    await this.setEngineValue(engineKey, 'RCHWHE', outputs.heatOfExtraction);
    await this.setEngineValue(engineKey, 'RCHWHA', outputs.heatOfExtraction);
    await this.setEngineValue(engineKey, 'RCHWIP', outputs.powerInput);
    await this.setEngineValue(engineKey, 'RCHWEFF', outputs.eer);
    await this.setEngineValue(engineKey, 'RCHWCOP', outputs.cop);
    await this.setEngineValue(engineKey, 'RCHLEWT', outputs.loadEnteringWaterTemperature);
    await this.setEngineValue(engineKey, 'RCHLFR', outputs.loadFluidFlow);
    await this.setEngineValue(engineKey, 'RCHLLWT', outputs.loadLeavingWaterTemperature);
    await this.setEngineValue(engineKey, 'RCHLPD', outputs.loadPressureDrop);
  }

  private async setCoolingResponseValues(engineKey: string, outputs: ClmPerformanceOutput): Promise<void> {
    await this.setEngineValue(engineKey, 'RCCWFR', outputs.fluidFlow);
    await this.setEngineValue(engineKey, 'RCCFAirflow', outputs.airflow);
    await this.setEngineValue(engineKey, 'RCCTot', outputs.totalCapacity);
    await this.setEngineValue(engineKey, 'RCCSens', outputs.sensibleCapacity);
    await this.setEngineValue(engineKey, 'RCCLDB', outputs.leavingDryBulbTemperature);
    await this.setEngineValue(engineKey, 'RCCLWB', outputs.leavingWetBulbTemperature);
    await this.setEngineValue(engineKey, 'RCCWPD', outputs.pressureDrop);
    await this.setEngineValue(engineKey, 'RCCLWT', outputs.leavingWaterTemperature);
    await this.setEngineValue(engineKey, 'RCCWHE', outputs.heatOfExtraction);
    await this.setEngineValue(engineKey, 'RCCWHR', outputs.heatOfRejection);
    await this.setEngineValue(engineKey, 'RCCWIP', outputs.powerInput);
    await this.setEngineValue(engineKey, 'RCCWEFF', outputs.eer);
    await this.setEngineValue(engineKey, 'RCCWCOP', outputs.cop);
    await this.setEngineValue(engineKey, 'RCCLEWT', outputs.loadEnteringWaterTemperature);
    await this.setEngineValue(engineKey, 'RCCLFR', outputs.loadFluidFlow);
    await this.setEngineValue(engineKey, 'RCCLLWT', outputs.loadLeavingWaterTemperature);
    await this.setEngineValue(engineKey, 'RCCLPD', outputs.loadPressureDrop);
  }

  private async buildPerformanceInput(engineKey: string, selections: GuruEntity[]): Promise<ClmPerformanceInput> {
    const input: ClmPerformanceInput = new ClmPerformanceInput();
    input.unitFamily = this.getSelectionValue(selections, 'USModel');
    input.unitSize = this.getSelectionValue(selections, 'USSize');
    input.voltage = this.getSelectionValue(selections, 'USUnitVoltage');
    input.blowerType = await this.getEngineCharacteristicValue(engineKey, 'USBlowerMotor', selections, 'MotorType');
    input.chassisStyleType = this.getSelectionValue(selections, 'USCHSStyle');
    input.waterCircuitType = this.getSelectionValue(selections, 'USWaterCircuitOptions');
    input.extendedOptionType = this.getSelectionValue(selections, 'USExtendedOptions');
    input.actualAirflow = this.getSelectionValue(selections, 'USCFAirflow');
    input.actualAirflowHeating = this.getSelectionValue(selections, 'USHFAirflow');
    input.enteringDryBulbTemperatureCooling = this.getSelectionValue(selections, 'USCEDB');
    input.enteringWetBulbTemperatureCooling = this.getSelectionValue(selections, 'USCEWB');
    input.enteringWaterTemperatureCooling = this.getSelectionValue(selections, 'USCEWT');
    input.performanceMethod = this.getSelectionValue(selections, 'USCWI');
    input.totalCapacityCooling = this.getSelectionValue(selections, 'USCWTot');
    input.sensibleCapacityCooling = this.getSelectionValue(selections, 'USCWSens');
    input.leavingDryBulbTemperatureCooling = this.getSelectionValue(selections, 'USCLDB');
    input.fluidFlowCooling = this.getSelectionValue(selections, 'USCWFR');
    input.enteringDryBulbTemperatureHeating = this.getSelectionValue(selections, 'USHEDB');
    input.enteringWaterTemperatureHeating = this.getSelectionValue(selections, 'USHEWT');
    input.totalCapacityHeating = this.getSelectionValue(selections, 'USHWTot');
    input.altitude = this.getSelectionValue(selections, 'USAltitude');
    input.fluidType = this.getSelectionValue(selections, 'USCFluid');
    input.concentration = this.getSelectionValue(selections, 'USCFluidMix');
    input.desiredWaterflow = this.getSelectionValue(selections, 'USDFR');
    input.desiredWaterflowTolerance = this.getSelectionValue(selections, 'USDFRTOL');
    input.totalCapacityCoolingLowerTolerance = this.getSelectionValue(selections, 'USCWTotLT');
    input.totalCapacityCoolingUpperTolerance = this.getSelectionValue(selections, 'USCWTotUT');
    input.totalCapacityHeatingLowerTolerance = this.getSelectionValue(selections, 'USHWTotLT');
    input.totalCapacityHeatingUpperTolerance = this.getSelectionValue(selections, 'USHWTotUT');
    input.sensibleCapacityLowerTolerance = this.getSelectionValue(selections, 'USCWSensLT');
    input.sensibleCapacityUpperTolerance = this.getSelectionValue(selections, 'USCWSensUT');
    input.leavingAirDryBulbTemperatureLowerTolerance = this.getSelectionValue(selections, 'USCLDBLT');
    input.leavingAirDryBulbTemperatureUpperTolerance = this.getSelectionValue(selections, 'USCLDBUT');
    input.coolingActualAirflowLowerTolerance = this.getSelectionValue(selections, 'USCFAirflowLT');
    input.coolingActualAirflowUpperTolerance = this.getSelectionValue(selections, 'USCFAirflowUT');
    input.heatingActualAirflowLowerTolerance = this.getSelectionValue(selections, 'USHFAirflowLT');
    input.heatingActualAirflowUpperTolerance = this.getSelectionValue(selections, 'USHFAirflowUT');
    input.fluidFlowTolerance = this.getSelectionValue(selections, 'USCWFRTOL');
    input.minimumEer = this.getSelectionValue(selections, 'USMinimumEer');
    input.externalStaticPressure = this.getSelectionValue(selections, 'USFESP');
    input.loadType = this.getSelectionValue(selections, 'USLoadType');
    input.fanSpeedProfile = this.getSelectionValue(selections, 'USFanSpeed');
    input.loadEnteringWaterTemperatureCooling = this.getSelectionValue(selections, "USCLEWT");
    input.loadEnteringWaterTemperatureHeating = this.getSelectionValue(selections, "USHLEWT");
    input.fluidFlowLoad = this.getSelectionValue(selections, "USLFR");
    return input;
  }

  private async getEngineCharacteristicValue(engineKey: string, entityName: string, selections: GuruEntity[], characteristicName: string): Promise<string> {
    const entity: GuruEntity = SelectionUtils.getEntity(selections, entityName);
    if (StringUtils.isNotBlank(entity?.Name)) {
      return await firstValueFrom<string>(this.modelService.getCharacteristicValue(engineKey, entityName, characteristicName));
    }
    return '';
  }

  private getSelectionValue(selections: GuruEntity[], selectionName: string): string {
    const entity: GuruEntity = SelectionUtils.getEntity(selections, selectionName);
    if (StringUtils.isNotBlank(entity?.Name)) {
      return entity.Value;
    }
    return '';
  }

  private getPerformanceResponse(performanceInput: ClmPerformanceInput): Observable<ClmPerformanceResponse> {
    const url: string = this.baseUrl + 'getperformance';
    const body: string = JSON.stringify(performanceInput);
    return this.http.post<ClmPerformanceResponse>(url, body, {headers: {'Content-Type': 'application/json'}});
  }

}
